import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Heart, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  // We use the shared auth context to bootstrap `user` after login so the app
  // treats the admin as logged in. But we call the admin-only endpoint to
  // enforce role check on the server.
  const { } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem("hh_token", data.token);
      toast.success("Welcome, Kristy.");
      window.location.href = "/admin"; // hard reload so AuthContext rebootstraps with token
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-40" />
      <form onSubmit={submit} className="relative z-10 w-full max-w-md rounded-3xl bg-black/60 backdrop-blur-xl border border-white/10 p-10" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-brand-pink/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-brand-pink" />
          </div>
          <div>
            <div className="font-display text-xl">Host <span className="font-script text-brand-pink">Hustle</span></div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">Admin Console</div>
          </div>
        </div>
        <div className="uppercase tracking-[0.3em] text-xs text-brand-pink mb-3">Admin Only</div>
        <h1 className="font-display text-3xl mb-8">Sign in to your console.</h1>
        <div className="space-y-5">
          <div>
            <Label className="mb-2 block text-white/80">Email</Label>
            <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-full h-12 px-5 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
          </div>
          <div>
            <Label className="mb-2 block text-white/80">Password</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-full h-12 px-5 bg-white/5 border-white/10 text-white" />
          </div>
        </div>
        <Button disabled={loading} type="submit" size="lg" data-testid="admin-submit" className="w-full mt-8 rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white btn-pink-glow">
          {loading ? "Signing in…" : "Enter console"}
        </Button>
        <p className="text-xs text-white/40 mt-6 text-center">
          <Heart className="w-3 h-3 inline text-brand-pink mr-1" fill="#E91E63" /> Restricted to Host Hustle staff.
        </p>
      </form>
    </div>
  );
}
